import { computed, defineComponent, ref, watch, type SetupContext } from "vue";
import html_iway from "./template.iway.html?raw";

const app_theme = import.meta.env.VITE_APP_THEME;
let html = "";
if ("iway" == app_theme) {
  html = html_iway;
} else if ("lic" == app_theme) {
  html = html_iway;
} else if ("sak" == app_theme) {
  html = html_iway;
}

const CH_DIAL_CODE = "41";

// Schweizer nationale Nummer inkl. führender 0, z.B. "0987654321" -> maximal 10 Ziffern
const MAX_SWISS_NATIONAL_DIGITS = 10;

// Bekannte ausländische Vorwahlen für die schöne Darstellung "+{prefix} {4} {4} {2} {2}".
// Nach Länge absteigend, damit z.B. +423 (Liechtenstein) nicht faelschlicherweise
// als AT/DE (2-stellige Codes) erkannt wird. Andere, hier nicht gelistete Vorwahlen
// werden unformatiert (aber trotzdem als internationale Nummer) übernommen.
const OTHER_DIAL_CODES = ["423", "43", "49", "33", "39"];

const matchOtherDialCode = (digitsAfterPlus: string): string | null =>
  OTHER_DIAL_CODES.find(
    (code) => digitsAfterPlus.startsWith(code) && digitsAfterPlus.length > code.length,
  ) ?? null;

// Schweizer Darstellung: nationale Nummer mit führender 0, z.B. "098 765 43 21"
const formatSwiss = (digits: string): string => {
  const parts = [
    digits.slice(0, 3),
    digits.slice(3, 6),
    digits.slice(6, 8),
    digits.slice(8, 10),
  ].filter((part) => part.length > 0);
  let rest = digits.slice(10);
  while (rest.length > 0) {
    parts.push(rest.slice(0, 2));
    rest = rest.slice(2);
  }
  return parts.join(" ");
};

// Darstellung anderer Länder: "+{prefix} {4} {4} {2} {2}"
const formatOther = (dialCode: string, significant: string): string => {
  const parts = [
    significant.slice(0, 4),
    significant.slice(4, 8),
    significant.slice(8, 10),
    significant.slice(10, 12),
  ].filter((part) => part.length > 0);
  let rest = significant.slice(12);
  while (rest.length > 0) {
    parts.push(rest.slice(0, 2));
    rest = rest.slice(2);
  }
  return `+${dialCode}` + (0 < parts.length ? " " + parts.join(" ") : "");
};

// Baut aus der aktuellen Anzeige den zu übermittelnden Wert - immer eine internationale
// Nummer mit "+", auch für Schweizer Nummern (die national dargestellt werden)
const deriveE164 = (display: string): string | null => {
  const trimmed = display.trim();
  if (trimmed.startsWith("+")) {
    const digitsAfterPlus = trimmed.slice(1).replace(/\D/g, "");
    return 0 < digitsAfterPlus.length ? `+${digitsAfterPlus}` : null;
  }
  const significant = trimmed.replace(/\D/g, "").replace(/^0+/, "");
  return 0 < significant.length ? `+${CH_DIAL_CODE}${significant}` : null;
};

// Wandelt einen gespeicherten internationalen Wert (z.B. +41987654321) in die Anzeige um
const toDisplay = (value: string | null): string => {
  const raw = (value ?? "").trim();
  if (!raw.startsWith("+")) {
    const digits = raw.replace(/\D/g, "").slice(0, MAX_SWISS_NATIONAL_DIGITS);
    return 0 < digits.length ? formatSwiss(digits) : "";
  }
  const digitsAfterPlus = raw.slice(1).replace(/\D/g, "");
  if (digitsAfterPlus.startsWith(CH_DIAL_CODE) && digitsAfterPlus.length >= CH_DIAL_CODE.length) {
    const significant = digitsAfterPlus.slice(CH_DIAL_CODE.length);
    return formatSwiss(("0" + significant).slice(0, MAX_SWISS_NATIONAL_DIGITS));
  }
  const matchedDialCode = matchOtherDialCode(digitsAfterPlus);
  if (matchedDialCode) {
    return formatOther(matchedDialCode, digitsAfterPlus.slice(matchedDialCode.length));
  }
  return `+${digitsAfterPlus}`;
};

let instanceCounter = 0;

interface Props {
  modelValue: string | null;
  label: string;
  error: string;
  className: string;
  swissOnly: boolean;
}

export default defineComponent({
  template: html,
  props: {
    modelValue: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      required: true,
    },
    error: {
      type: String,
      default: "",
    },
    className: {
      type: String,
      default: "",
    },
    // Wenn gesetzt, werden nur schweizerische Nummern akzeptiert
    swissOnly: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    "update:modelValue": (_value: string | null) => true,
    change: () => true,
  },
  setup(props: Props, { emit }: SetupContext) {
    // eindeutige ID, da diese Komponente mehrfach gleichzeitig gerendert werden kann
    const inputId = `international-number-entry-${++instanceCounter}`;

    const displayValue = ref<string>(toDisplay(props.modelValue));

    watch(() => props.modelValue, (value) => {
      if (value != deriveE164(displayValue.value)) {
        displayValue.value = toDisplay(value);
      }
    });

    // Wechselt swissOnly zur Laufzeit auf true, während eine nicht-schweizerische Nummer erfasst war -> zurücksetzen
    watch(() => props.swissOnly, (swissOnly) => {
      if (swissOnly && displayValue.value.startsWith("+")) {
        const digitsAfterPlus = displayValue.value.slice(1).replace(/\D/g, "");
        if (!digitsAfterPlus.startsWith(CH_DIAL_CODE)) {
          displayValue.value = "";
          emit("update:modelValue", null);
        }
      }
    });

    const emitValue = () => {
      emit("update:modelValue", deriveE164(displayValue.value));
    };

    // Während der Eingabe wird nicht validiert (nur formatiert) - Fehler kommen ausschliesslich vom Server
    const displayedError = computed(() => props.error);

    // Formatiert während der Eingabe; erkennt eine direkt eingegebene internationale Nummer
    // (z.B. +41... oder 0041...) und stellt Schweizer Nummern national, andere international dar
    const onInput = (evt: Event) => {
      const target = evt.target as HTMLInputElement;
      let raw = target.value;

      if (raw.startsWith("00")) {
        raw = "+" + raw.slice(2);
      }

      if (raw.startsWith("+")) {
        // Doppeltes "+" (z.B. durch Autofill) bereinigen: "++41..." -> "+41...", sonst "++..." -> "+..."
        raw = raw.replace(/^\+{2,}/, "+");
        const digitsAfterPlus = raw.slice(1).replace(/\D/g, "");

        if (digitsAfterPlus.startsWith(CH_DIAL_CODE) && digitsAfterPlus.length >= CH_DIAL_CODE.length) {
          // "+41" wird sofort auf die führende 0 korrigiert, auch ohne weitere Ziffern danach
          const significant = digitsAfterPlus.slice(CH_DIAL_CODE.length);
          displayValue.value = formatSwiss(("0" + significant).slice(0, MAX_SWISS_NATIONAL_DIGITS));
          target.value = displayValue.value;
          emitValue();
          return;
        }

        if (props.swissOnly) {
          // In swissOnly-Feldern werden nicht-schweizerische Nummern nicht übernommen
          target.value = raw;
          displayValue.value = raw;
          return;
        }

        const matchedDialCode = matchOtherDialCode(digitsAfterPlus);
        if (matchedDialCode) {
          displayValue.value = formatOther(matchedDialCode, digitsAfterPlus.slice(matchedDialCode.length));
          target.value = displayValue.value;
          emitValue();
          return;
        }

        // Unbekannte Vorwahl: unformatiert übernehmen, aber trotzdem als internationale Nummer übermitteln
        displayValue.value = raw;
        target.value = raw;
        emitValue();
        return;
      }

      const digits = raw.replace(/\D/g, "").slice(0, MAX_SWISS_NATIONAL_DIGITS);
      displayValue.value = formatSwiss(digits);
      target.value = displayValue.value;
      emitValue();
    };

    const onChange = () => {
      emit("change");
    };

    return {
      inputId,
      displayValue,
      displayedError,
      onInput,
      onChange,
    };
  },
} as any);
